/**
 * Printing (SRS 14.4).
 *
 * The API returns a complete, self-contained HTML document — letterhead,
 * styles and barcodes included — or a base64 PDF when a Chromium renderer is
 * available. Either way the document itself is authoritative; this only puts it
 * in front of a printer.
 *
 * A hidden iframe rather than a popup: pop-up blockers are common on shared
 * desk machines, and losing a prescription to one is not acceptable.
 */
export const PRINT_FRAME_SANDBOX = 'allow-same-origin allow-modals';

export const FONT_WAIT_MS = 2_000;

export function printDocument(html: string): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', PRINT_FRAME_SANDBOX);
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  if (!doc) {
    document.body.removeChild(frame);
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  const trigger = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    // Removed on a delay: Safari cancels the job if the frame goes first.
    setTimeout(() => frame.remove(), 1_000);
  };

  const afterFonts = () => {
    const fonts = frame.contentWindow?.document.fonts;
    if (!fonts) {
      setTimeout(trigger, 60);
      return;
    }
    void Promise.race([fonts.ready, new Promise((resolve) => setTimeout(resolve, FONT_WAIT_MS))]).then(() => setTimeout(trigger, 60));
  };

  if (frame.contentWindow?.document.readyState === 'complete') {
    afterFonts();
  } else {
    frame.onload = afterFonts;
  }
}

/** Opens a returned PDF (base64) in a new tab, falling back to the HTML. */
export function openDocument(result: { html: string; pdfBase64: string | null; filename: string }): void {
  if (!result.pdfBase64) {
    printDocument(result.html);
    return;
  }
  const bytes = Uint8Array.from(atob(result.pdfBase64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
